import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * GMC Hummer EV3x (linkId 2382) — SUV and Pickup body styles.
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2382.ts`; shared UI boilerplate
 * (staticTranslations / dealers / saveFormConfig / externalScript) follows the
 * 2378 project. `styleConfig` reuses the GMC palette + `Stratum GMC` face
 * verbatim from 2385 (Canyon), the first GMC project in 2.0.
 *
 * Conversion notes:
 *  - 1.0 `brandItemCode` becomes `autoIntelRPOCode` (variants, colours,
 *    accessories, packages) — key omitted where 1.0 leaves it blank.
 *  - 1.0 `removeAccessories` becomes graphics `groupId` + `isDefault`, derived
 *    per body style since 1.0's numeric accessory ids restart in each variant's
 *    array: `{Opaque_Roof, Infinity Roof}` and
 *    `{WO_Extreme_Off_Road_Package, W_Extreme_Off_Road_Package}` per body style.
 *  - 1.0 `interactionApplyMessage` becomes `affectedInteractionOnApply`, so
 *    equipping the off-road package snaps the tailgate shut.
 *  - Packages carry `accessoryIds` mapped from 1.0's numeric
 *    `package.accessoryIDs` to the 2.0 accessory keys above.
 *  - `hotspots` is model-level (not per-variant — see the graphics-metadata
 *    header), built from the default SUV variant's two seat rows.
 *  - 1.0's Off Road Package roof accessory (id 4/5, both body styles) points at
 *    `Denali_Roof_Dual_Tone_Ebony_Twillight_Metallic.jpg` — a stale
 *    cross-model reference (an Acadia/Denali asset, 404s on Hummer's own
 *    deployment). Each body style's own Infinity Roof still stands in.
 *  - 1.0's `engine`/`transmission` filterOptions (Petrol/Turbo Petrol,
 *    CVT/MT/AMT) are generic boilerplate that doesn't describe an EV SUV/Pickup
 *    pair — dropped, same call as Canyon's disabled price-range filter.
 *  - `toggleTabs` hides CONFIG (no real filters, so it would show "No filters
 *    available.") — 2384 precedent.
 *
 * Assets live in public/assets/{images,videos}/2382/, mirroring the 1.0
 * SUV_* / SUT_* filename layout, resolved by `assetSrc()` in
 * src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Hummer: {
      variants: {
        suv: {
          configurationId: 'mlfv94crqnmb',
          name: {
            en: 'SUV',
            ar: 'Hummer EV3x SUV',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'SUV',
          highlights: [
            {
              en: '830HP (SUV)/1000HP (PICKUP)',
              ar: '830 حصان (الدفع الرباعي)  / 1000 حصان (بيك أب)',
            },
            {
              en: 'Torque 15,592NM (SUV)/15,592NM (PICKUP)',
              ar: 'عزم الدوران 15,592 نيوتن متر (الدفع الرباعي) / 15,592 نيوتن متر (بيك أب)',
            },
            {
              en: '*Range 480KM (SUV)/529KM (PICKUP)',
              ar: '*المدى 480 كم (الدفع الرباعي) / 529 كم (بيك أب)',
            },
          ],
          specs: [
            {
              en: '830HP (SUV)/1000HP (PICKUP)',
              ar: '830 حصان (الدفع الرباعي)  / 1000 حصان (بيك أب)',
            },
            {
              en: '15,592NM (SUV)/15,592NM (PICKUP)',
              ar: '15,592 نيوتن متر (الدفع الرباعي) / 15,592 نيوتن متر (بيك أب)',
            },
          ],
          image: 'images/2382/SUV_variant.png',
          price: {
            KSA: 528872,
            UAE: 519000,
            OMR: 55003,
            BHD: 53592,
            QAR: 513359,
            JOD: 100133,
            KW: 43720,
            LBP: 141033,
            IQD: 141033,
            default: 519000,
          },
          basePrice: {
            UAE: 519000,
            KSA: 579200,
            OMR: 51800,
            KW: 41100,
            LBP: 189537,
            JOD: 164800,
            BHD: 53371,
            IQD: 185062328,
            QAR: 519086,
            default: 519000,
          },
          index: 0,
          isDefault: true,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SG',
        },
        pickup: {
          configurationId: 'mlfv9409crqnmb',
          name: {
            en: 'PICKUP',
            ar: 'Hummer EV3x PICKUP',
          },
          description: {
            en: '',
            ar: '',
          },
          drivetrain: '4WD',
          trim: 'PICKUP',
          highlights: [
            {
              en: '830HP (SUV)/1000HP (PICKUP)',
              ar: '830 حصان (الدفع الرباعي)  / 1000 حصان (بيك أب)',
            },
            {
              en: 'Torque 15,592NM (SUV)/15,592NM (PICKUP)',
              ar: 'عزم الدوران 15,592 نيوتن متر (الدفع الرباعي) / 15,592 نيوتن متر (بيك أب)',
            },
            {
              en: '*Range 480KM (SUV)/529KM (PICKUP)',
              ar: '*المدى 480 كم (الدفع الرباعي) / 529 كم (بيك أب)',
            },
          ],
          specs: [
            {
              en: '830HP (SUV)/1000HP (PICKUP)',
              ar: '830 حصان (الدفع الرباعي)  / 1000 حصان (بيك أب)',
            },
            {
              en: '15,592NM (SUV)/15,592NM (PICKUP)',
              ar: '15,592 نيوتن متر (الدفع الرباعي) / 15,592 نيوتن متر (بيك أب)',
            },
          ],
          image: 'images/2382/SUT_Variant.png',
          price: {
            KSA: 539062,
            UAE: 529000,
            OMR: 56062,
            BHD: 54625,
            QAR: 523250,
            JOD: 102062,
            KW: 44562,
            LBP: 143750,
            IQD: 143750,
            default: 529000,
          },
          basePrice: {
            UAE: 529000,
            KSA: 590600,
            KW: 42000,
            JOD: 164800,
            LBP: 189537,
            BHD: 54387,
            OMR: 51800,
            IQD: 188628076,
            QAR: 529087,
            default: 529000,
          },
          index: 1,
          isDefault: false,
          ar: {
            usdz: '',
            enable: false,
          },
          toggleTabs: {
            CONFIG: {
              mobile: false,
              desktop: false,
            },
            MODEL: {
              mobile: true,
              desktop: true,
            },
            EXTERIOR: {
              mobile: true,
              desktop: true,
            },
            INTERIOR: {
              mobile: true,
              desktop: true,
            },
            OPTIONS: {
              mobile: true,
              desktop: true,
            },
            SUMMARY: {
              mobile: true,
              desktop: true,
            },
          },
          autoIntelRPOCode: '1SD',
        },
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2382/CitySample_2.png',
          title: {
            en: 'City Landscape 1',
            ar: 'منظر المدينة 1',
          },
        },
        g0ZA968_RpUe: {
          image: 'images/2382/gcc_desert_env.png',
          title: {
            en: 'Desert Landscape',
            ar: 'مشهد صحراوي',
          },
        },
      },
      ambientLighting: {},
      colors: {
        exterior: {
          g946ignut6zm: {
            label: {
              en: 'Void Black',
              ar: 'أسود فويد',
            },
            image: 'images/2382/SUV_Void_Black.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX98',
          },
          'g946ignut6zm-pickup': {
            label: {
              en: 'Void Black',
              ar: 'أسود فويد',
            },
            image: 'images/2382/SUT_Void_Black.png',
            usdz: 'red_black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX98',
          },
          '5zwfz8t9v1ci': {
            label: {
              en: 'Tide Metallic',
              ar: 'تايد معدني',
            },
            image: 'images/2382/SUV_Tide_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G7X',
            brandItemCode: 'EX94',
          },
          '5zwfz8t9v1ci-pickup': {
            label: {
              en: 'Tide Metallic',
              ar: 'تايد معدني',
            },
            image: 'images/2382/SUT_Tide_Metallic.png',
            usdz: 'black',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'G7X',
            brandItemCode: 'EX94',
          },
          '3bcwcix9d6ow': {
            label: {
              en: 'Interstellar White',
              ar: 'أبيض فلكي',
            },
            image: 'images/2382/SUV_Interstellar_White.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX77',
          },
          '3bcwcix9d6ow-pickup': {
            label: {
              en: 'Interstellar White',
              ar: 'أبيض فلكي',
            },
            image: 'images/2382/SUT_Interstellar_White.png',
            usdz: 'red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX77',
          },
          ekrnov29ywox: {
            label: {
              en: 'Meteorite Metallic',
              ar: 'نيزكي معدني',
            },
            image: 'images/2382/SUV_Meteorite_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX79',
          },
          'ekrnov29ywox-pickup': {
            label: {
              en: 'Meteorite Metallic',
              ar: 'نيزكي معدني',
            },
            image: 'images/2382/SUT_Meteorite_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GNO',
            brandItemCode: 'EX79',
          },
          t97r2gqldscc: {
            label: {
              en: 'Afterburner Tintcoat',
              ar: 'أفتر بيرنر بطبقتين',
            },
            image: 'images/2382/SUV_Afterburner_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GC5',
            brandItemCode: 'EX55',
          },
          't97r2gqldscc-pickup': {
            label: {
              en: 'Afterburner Tintcoat',
              ar: 'أفتر بيرنر بطبقتين',
            },
            image: 'images/2382/SUT_Afterburner_Tintcoat.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GC5',
            brandItemCode: 'EX55',
          },
          e12d2rs52b1b: {
            label: {
              en: 'Supernova Metallic',
              ar: 'سوبر نوفا معدني',
            },
            image: 'images/2382/SUV_Supernova_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GKK',
            brandItemCode: 'EX93',
          },
          'e12d2rs52b1b-pickup': {
            label: {
              en: 'Supernova Metallic',
              ar: 'سوبر نوفا معدني',
            },
            image: 'images/2382/SUT_Supernova_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GKK',
            brandItemCode: 'EX93',
          },
          e12d2rrrs52b1b: {
            label: {
              en: 'Deep Aurora Metallic',
              ar: 'أورورا معدني داكن',
            },
            image: 'images/2382/SUV_Deep_Aurora_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXN',
            brandItemCode: 'EX67',
          },
          'e12d2rrrs52b1b-pickup': {
            label: {
              en: 'Deep Aurora Metallic',
              ar: 'أورورا معدني داكن',
            },
            image: 'images/2382/SUT_Deep_Aurora_Metallic.png',
            usdz: 'blue',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              KW: 0,
              default: 0,
            },
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'GXN',
            brandItemCode: 'EX67',
          },
        },
        interior: {
          d4xeirch0we2: {
            label: {
              en: 'Lunar Horizon',
              ar: 'الأفق القمري',
            },
            image: 'images/2382/SUV_Lunar_Horizon.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H9Z',
            brandItemCode: 'IN59',
          },
          'd4xeirch0we2-pickup': {
            label: {
              en: 'Lunar Horizon',
              ar: 'الأفق القمري',
            },
            image: 'images/2382/SUT_Lunar_Horizon.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'H9Z',
            brandItemCode: 'IN59',
          },
          '5vktfJxuscn42': {
            label: {
              en: 'Lunar Shadow',
              ar: 'الظل القمري',
            },
            image: 'images/2382/SUV_Lunar_Shadow.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HAS',
            brandItemCode: 'IN60',
          },
          '5vktfJxuscn42-pickup': {
            label: {
              en: 'Lunar Shadow',
              ar: 'الظل القمري',
            },
            image: 'images/2382/SUT_Lunar_Shadow.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HAS',
            brandItemCode: 'IN60',
          },
        },
      },
      features: {
        '3h3fjg5zh489': {
          label: {
            en: 'Illuminated Hummer grille with animted lighting',
            ar: 'شبك أمامي مضيء خاص بمركبة هامر مع إضاءة متحركة',
          },
          image: 'images/2382/SUV_Grille.jpg',
          title: {
            en: 'Illuminated Hummer grille with animted lighting',
            ar: 'شبك أمامي مضيء خاص بمركبة هامر مع إضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        '3h3fjg5zh489-pickup': {
          label: {
            en: 'Illuminated Hummer grille with animted lighting',
            ar: 'شبك أمامي مضيء خاص بمركبة هامر مع إضاءة متحركة',
          },
          image: 'images/2382/SUT_Grille.jpg',
          title: {
            en: 'Illuminated Hummer grille with animted lighting',
            ar: 'شبك أمامي مضيء خاص بمركبة هامر مع إضاءة متحركة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        ngs1smpm2k89: {
          label: {
            en: 'Infinity Roof Transparent Panels',
            ar: 'ألواح سقف شفافة لا متناهية',
          },
          image: 'images/2382/SUV_Roof.jpg',
          title: {
            en: 'Infinity Roof Transparent Panels',
            ar: 'ألواح سقف شفافة لا متناهية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'ngs1smpm2k89-pickup': {
          label: {
            en: 'Infinity Roof Removable Panels',
            ar: 'ألواح سقف لا متناهية قابلة للإزالة',
          },
          image: 'images/2382/SUT_Roof.jpg',
          title: {
            en: 'Infinity Roof Removable Panels',
            ar: 'ألواح سقف لا متناهية قابلة للإزالة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        slfnh3hjd6s8: {
          label: {
            en: 'Marker Lights',
            ar: 'أضواء جانبية',
          },
          image: 'images/2382/SUV_Marker_Lights.jpg',
          title: {
            en: 'Marker Lights',
            ar: 'أضواء جانبية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'slfnh3hjd6s8-pickup': {
          label: {
            en: 'Marker Lights',
            ar: 'أضواء جانبية',
          },
          image: 'images/2382/SUT_Marker_Lights.jpg',
          title: {
            en: 'Marker Lights',
            ar: 'أضواء جانبية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        zeq7pry7mouo: {
          label: {
            en: 'Power - ETRUNK',
            ar: 'صندوق أمامي كهربائي ETRUNK',
          },
          image: 'images/2382/SUV_ETRUNK.jpg',
          title: {
            en: 'Power - ETRUNK',
            ar: 'صندوق أمامي كهربائي ETRUNK',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'zeq7pry7mouo-pickup': {
          label: {
            en: 'Power - ETRUNK',
            ar: 'صندوق أمامي كهربائي ETRUNK',
          },
          image: 'images/2382/SUT_ETRUNK.jpg',
          title: {
            en: 'Power - ETRUNK',
            ar: 'صندوق أمامي كهربائي ETRUNK',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        marg8otdccyk: {
          label: {
            en: '22 inch All Terrain Tires',
            ar: 'إطارات لجميع التضاريس مقاس 22 إنش',
          },
          image: 'images/2382/SUV_Alloys.jpg',
          title: {
            en: '22 inch All Terrain Tires',
            ar: 'إطارات لجميع التضاريس مقاس 22 إنش',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'marg8otdccyk-pickup': {
          label: {
            en: '22 inch All Terrain Tires',
            ar: 'إطارات لجميع التضاريس مقاس 22 إنش',
          },
          image: 'images/2382/SUT_Alloys.jpg',
          title: {
            en: '22 inch All Terrain Tires',
            ar: 'إطارات لجميع التضاريس مقاس 22 إنش',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        marg8ffotdccyk: {
          label: {
            en: 'LED Center Brake Lights',
            ar: 'أضواء الفرامل المركزية LED',
          },
          image: 'images/2382/SUV_Brake_Lights.jpg',
          title: {
            en: 'LED Center Brake Lights',
            ar: 'أضواء الفرامل المركزية LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'marg8ffotdccyk-pickup': {
          label: {
            en: 'LED Center Brake Lights',
            ar: 'أضواء الفرامل المركزية LED',
          },
          image: 'images/2382/SUT_Brake_Lights.jpg',
          title: {
            en: 'LED Center Brake Lights',
            ar: 'أضواء الفرامل المركزية LED',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        mporg8ffotdccyk: {
          label: {
            en: 'Adaptive Air Ride',
            ar: 'نظام تعليق هوائي متكيف',
          },
          image: 'images/2382/SUV_Adaptive_Air_Ride.jpg',
          title: {
            en: 'Adaptive Air Ride',
            ar: 'نظام تعليق هوائي متكيف',
          },
          desc: {
            en: '4 corner Adaptive Air Ride with up to 6inch of lift in Extract mode',
            ar: 'نظام تعليق هوائي متكيف رباعي الزوايا مع رفع يصل إلى 6 إنش في وضع Extract',
          },
          type: 'exterior',
        },
        'mporg8ffotdccyk-pickup': {
          label: {
            en: 'Adaptive Air Ride',
            ar: 'نظام تعليق هوائي متكيف',
          },
          image: 'images/2382/SUT_Adaptive_Air_Ride.jpg',
          title: {
            en: 'Adaptive Air Ride',
            ar: 'نظام تعليق هوائي متكيف',
          },
          desc: {
            en: '4 corner Adaptive Air Ride with up to 6inch of lift in Extract mode',
            ar: 'نظام تعليق هوائي متكيف رباعي الزوايا مع رفع يصل إلى 6 إنش في وضع Extract',
          },
          type: 'exterior',
        },
        marg8otdkoccyk: {
          label: {
            en: 'H Pattern Rear Taillights',
            ar: 'مصابيح خلفية بنمط حرف H',
          },
          image: 'images/2382/SUV_Taillights.jpg',
          title: {
            en: 'H Pattern Rear Taillights',
            ar: 'مصابيح خلفية بنمط حرف H',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'marg8otdkoccyk-pickup': {
          label: {
            en: 'H Pattern Rear Taillights',
            ar: 'مصابيح خلفية بنمط حرف H',
          },
          image: 'images/2382/SUT_Taillights.jpg',
          title: {
            en: 'H Pattern Rear Taillights',
            ar: 'مصابيح خلفية بنمط حرف H',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        marg8otddkoccyk: {
          label: {
            en: 'Charging Inlet',
            ar: 'مدخل الشحن',
          },
          image: 'images/2382/SUV_Charging_Inlet.jpg',
          title: {
            en: 'Charging Inlet',
            ar: 'مدخل الشحن',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'marg8otddkoccyk-pickup': {
          label: {
            en: 'Charging Inlet',
            ar: 'مدخل الشحن',
          },
          image: 'images/2382/SUT_Charging_Inlet.jpg',
          title: {
            en: 'Charging Inlet',
            ar: 'مدخل الشحن',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        kiarg8otddkoccyk: {
          label: {
            en: 'Drop Down Rear Glass',
            ar: 'زجاج خلفي قابل للإنزال',
          },
          image: 'images/2382/SUV_Rear_Glass.jpg',
          title: {
            en: 'Drop Down Rear Glass',
            ar: 'زجاج خلفي قابل للإنزال',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'kiarg8otddkoccyk-pickup': {
          label: {
            en: 'Drop Down Rear Glass',
            ar: 'زجاج خلفي قابل للإنزال',
          },
          image: 'images/2382/SUT_Rear_Glass.jpg',
          title: {
            en: 'Drop Down Rear Glass',
            ar: 'زجاج خلفي قابل للإنزال',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        kiiarg9otddkoccyk: {
          label: {
            en: 'Power swing Gate with full size Spare Tire',
            ar: 'باب خلفي متأرجح كهربائي مزود بإطار احتياطي كامل الحجم',
          },
          image: 'images/2382/SUV_Power_Swing_Gate.jpg',
          title: {
            en: 'Power swing Gate with full size Spare Tire',
            ar: 'باب خلفي متأرجح كهربائي مزود بإطار احتياطي كامل الحجم',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        'kiiarg9otddkoccyk-pickup': {
          label: {
            en: '49.7 Approach Angle in Extract Mode',
            ar: 'زاوية اقتراب 49.7 في وضع Extract',
          },
          image: 'images/2382/SUT_Approach_Angle.jpg',
          title: {
            en: '49.7 Approach Angle in Extract Mode',
            ar: 'زاوية اقتراب 49.7 في وضع Extract',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
        oxfitdgg14gk: {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2382/SUV_Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4 inch Infotainment Display',
            ar: 'شاشة معلومات وترفيه مقاس 13.4 إنش',
          },
          type: 'interior',
        },
        'oxfitdgg14gk-pickup': {
          label: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          image: 'images/2382/SUT_Infoscreen.jpg',
          title: {
            en: 'Infotainment System',
            ar: 'نظام المعلومات والترفيه',
          },
          desc: {
            en: '13.4 inch Infotainment Display',
            ar: 'شاشة معلومات وترفيه مقاس 13.4 إنش',
          },
          type: 'interior',
        },
        ppj9rls95crn: {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2382/SUV_Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3 inch Driver Information Display',
            ar: 'شاشة عرض معلومات السائق مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        'ppj9rls95crn-pickup': {
          label: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          image: 'images/2382/SUT_Speedometer.jpg',
          title: {
            en: 'Driver Information System',
            ar: 'نظام معلومات السائق',
          },
          desc: {
            en: '12.3 inch Driver Information Display',
            ar: 'شاشة عرض معلومات السائق مقاس 12.3 إنش',
          },
          type: 'interior',
        },
        okumo92efxcj: {
          label: {
            en: 'Rear View Video Mirror',
            ar: 'مرآة فيديو للرؤية الخلفية',
          },
          image: 'images/2382/SUV_IRVM.jpg',
          title: {
            en: 'Rear View Video Mirror',
            ar: 'مرآة فيديو للرؤية الخلفية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'okumo92efxcj-pickup': {
          label: {
            en: 'Rear View Video Mirror',
            ar: 'مرآة فيديو للرؤية الخلفية',
          },
          image: 'images/2382/SUT_IRVM.jpg',
          title: {
            en: 'Rear View Video Mirror',
            ar: 'مرآة فيديو للرؤية الخلفية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        z5e0bv2uqipu: {
          label: {
            en: 'H Pattern Air Vents',
            ar: 'فتحات تهوية بنمط حرف H',
          },
          image: 'images/2382/SUV_Air_Vents.jpg',
          title: {
            en: 'H Pattern Air Vents',
            ar: 'فتحات تهوية بنمط حرف H',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'z5e0bv2uqipu-pickup': {
          label: {
            en: 'H Pattern Air Vents',
            ar: 'فتحات تهوية بنمط حرف H',
          },
          image: 'images/2382/SUT_Air_Vents.jpg',
          title: {
            en: 'H Pattern Air Vents',
            ar: 'فتحات تهوية بنمط حرف H',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        z5ee0pbv2uqipu: {
          label: {
            en: 'Electronic Transmission Shifter',
            ar: 'ناقل حركة إلكتروني',
          },
          image: 'images/2382/SUV_ETS.jpg',
          title: {
            en: 'Electronic Transmission Shifter',
            ar: 'ناقل حركة إلكتروني',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'z5ee0pbv2uqipu-pickup': {
          label: {
            en: 'Electronic Transmission Shifter',
            ar: 'ناقل حركة إلكتروني',
          },
          image: 'images/2382/SUT_ETS.jpg',
          title: {
            en: 'Electronic Transmission Shifter',
            ar: 'ناقل حركة إلكتروني',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        p5ee0pbv2uqipu: {
          label: {
            en: 'Drive Mode 4-Wheel-Steer Suspension',
            ar: 'تعليق بنظام التوجيه الرباعي وأوضاع القيادة',
          },
          image: 'images/2382/SUV_Driver_Mode.jpg',
          title: {
            en: 'Drive Mode 4-Wheel-Steer Suspension',
            ar: 'تعليق بنظام التوجيه الرباعي وأوضاع القيادة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'p5ee0pbv2uqipu-pickup': {
          label: {
            en: 'Drive Mode 4-Wheel-Steer Suspension',
            ar: 'تعليق بنظام التوجيه الرباعي وأوضاع القيادة',
          },
          image: 'images/2382/SUT_Driver_Mode.jpg',
          title: {
            en: 'Drive Mode 4-Wheel-Steer Suspension',
            ar: 'تعليق بنظام التوجيه الرباعي وأوضاع القيادة',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        p7ee0pbv2uqipu: {
          label: {
            en: '14 Speaker BOSE Audio System',
            ar: 'نظام Bose الصوتي بـ 14 مكبر صوت',
          },
          image: 'images/2382/SUV_Bose_Speaker.jpg',
          title: {
            en: '14 Speaker BOSE Audio System',
            ar: 'نظام Bose الصوتي بـ 14 مكبر صوت',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'p7ee0pbv2uqipu-pickup': {
          label: {
            en: '14 Speaker BOSE Audio System',
            ar: 'نظام Bose الصوتي بـ 14 مكبر صوت',
          },
          image: 'images/2382/SUT_Bose_Speaker.jpg',
          title: {
            en: '14 Speaker BOSE Audio System',
            ar: 'نظام Bose الصوتي بـ 14 مكبر صوت',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        p7pe0pbv2dqipu: {
          label: {
            en: 'Seat Back Storage',
            ar: 'جيوب تخزين خلف المقاعد',
          },
          image: 'images/2382/SUV_Seat_Storage.jpg',
          title: {
            en: 'Seat Back Storage',
            ar: 'جيوب تخزين خلف المقاعد',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'p7pe0pbv2dqipu-pickup': {
          label: {
            en: 'Seat Back Storage',
            ar: 'جيوب تخزين خلف المقاعد',
          },
          image: 'images/2382/SUT_Seat_Storage.jpg',
          title: {
            en: 'Seat Back Storage',
            ar: 'جيوب تخزين خلف المقاعد',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        p7pe0pblv2dqipu: {
          label: {
            en: '40/60 flip up rear seat',
            ar: 'مقاعد خلفية قابلة للطي بنسبة 40/60',
          },
          image: 'images/2382/SUV_Rear_Seat.jpg',
          title: {
            en: '40/60 flip up rear seat',
            ar: 'مقاعد خلفية قابلة للطي بنسبة 40/60',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        'p7pe0pblv2dqipu-pickup': {
          label: {
            en: '40/60 flip up rear seat',
            ar: 'مقاعد خلفية قابلة للطي بنسبة 40/60',
          },
          image: 'images/2382/SUT_Rear_Seat.jpg',
          title: {
            en: '40/60 flip up rear seat',
            ar: 'مقاعد خلفية قابلة للطي بنسبة 40/60',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'interior',
        },
        sroplfnh3hjd6s8: {
          label: {
            en: 'Side Steps',
            ar: 'عتبات جانبية',
          },
          image: 'images/2382/SUT_Side_steps.jpg',
          title: {
            en: 'Side Steps',
            ar: 'عتبات جانبية',
          },
          desc: {
            en: '',
            ar: '',
          },
          type: 'exterior',
        },
      },
      accessories: {
        '8ylilfy10w5s': {
          label: {
            en: 'SUV_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '8ylilfy10w5s-pickup': {
          label: {
            en: 'SUT_EXT_Variant',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        whv4p5vjna3p: {
          label: {
            en: 'Opaque_Roof',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        gzf6oq39rp8k: {
          label: {
            en: 'WO_Extreme_Off_Road_Package_SUV',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        'gzf6oq39rp8k-pickup': {
          label: {
            en: 'WO_Extreme_Off_Road_Package_SUT',
            ar: '',
          },
          image: '',
          category: {
            en: 'Accessories',
            ar: 'الملحقات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        mbxtmb9ivqay: {
          label: {
            en: 'Infinity Roof, with removable transparent Sky Panels',
            ar: 'سقف لا متناهي، مع ألواح Sky شفافة قابلة للإزالة',
          },
          image: 'images/2382/SUV_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CF7',
          brandItemCode: 'AC193',
        },
        'mbxtmb9ivqay-pickup': {
          label: {
            en: 'Infinity Roof, with removable transparent Sky Panels',
            ar: 'سقف لا متناهي، مع ألواح Sky شفافة قابلة للإزالة',
          },
          image: 'images/2382/SUT_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'CF7',
          brandItemCode: 'AC193',
        },
        '58vsmxqqsfey': {
          label: {
            en: 'W_Extreme_Off_Road_Package_SUV',
            ar: '',
          },
          image: 'images/2382/SUV_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'Z6X',
        },
        '58vsmxqqsfey-pickup': {
          label: {
            en: 'W_Extreme_Off_Road_Package_SUT',
            ar: '',
          },
          image: 'images/2382/SUT_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          autoIntelRPOCode: 'Z6X',
        },
        '58v90smxqqsfey': {
          label: {
            en: 'New_Color_based_default',
            ar: '',
          },
          image: 'images/2382/SUV_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
        '58v90smxqqsfey-pickup': {
          label: {
            en: 'New_Color_based_default',
            ar: '',
          },
          image: 'images/2382/SUT_Infinity_Roof.jpg',
          category: {
            en: 'Roof',
            ar: 'السقف',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
        },
      },
      packages: {
        x7vtyriufcgr0: {
          label: {
            en: 'Extreme Off Road Package',
            ar: 'فئة الطرق الوعرة القصوى',
          },
          image: 'images/2382/SUV_package_skid_plat.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Extreme Off Road Package comes with 18" wheels, Underbody Armor, FR. Elockers/RR. Simulated Locker, Ultravision Camera System, Side Steps with Integrated Rock Sliders, and Rear O-Ring recovery hooks.',
            ar: 'تأتي حزمة الطرق الوعرة القصوى مع عجلات مقاس 18 إنش، ودروع أسفل الهيكل، وأقفال تفاضلية أمامية إلكترونية / قفل تفاضلي خلفي محاكى، ونظام كاميرا UltraVision، وعتبات جانبية مع منزلقات مدمجة للصخور، وحلقات سحب خلفية على شكل D.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '18" wheels',
                ar: 'عجلات مقاس 18 إنش',
              },
              accessoryDescription: {
                en: '18" wheels',
                ar: 'عجلات مقاس 18 إنش',
              },
              image: 'images/2382/SUV_package_wheel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Underbody Armor',
                ar: 'درع سفلي',
              },
              accessoryDescription: {
                en: 'Underbody Armor',
                ar: 'درع سفلي',
              },
              image: 'images/2382/SUV_package_skid_plat.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'FR. Elockers/RR. Simulated Locker',
                ar: 'أقفال تفاضلية أمامية كهربائية / قفل خلفي محاكى',
              },
              accessoryDescription: {
                en: 'FR. Elockers/RR. Simulated Locker',
                ar: 'أقفال تفاضلية أمامية كهربائية / قفل خلفي محاكى',
              },
              image: 'images/2382/SUV_elocker.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Ultravision Camera System',
                ar: 'نظام كاميرات UltraVision ',
              },
              accessoryDescription: {
                en: 'Ultravision Camera System',
                ar: 'نظام كاميرات UltraVision ',
              },
              image: 'images/2382/ultravision_camera.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Side Steps with Integrated Rock Sliders',
                ar: 'عتبات جانبية مزودة بألواح مدمجة للحماية من الصخور',
              },
              accessoryDescription: {
                en: 'Side Steps with Integrated Rock Sliders',
                ar: 'عتبات جانبية مزودة بألواح مدمجة للحماية من الصخور',
              },
              image: 'images/2382/SUV_package_sidesteps.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Rear D-Ring recovery hooks',
                ar: 'حلقات سحب خلفية على شكل حرف D',
              },
              accessoryDescription: {
                en: 'Rear D-Ring recovery hooks',
                ar: 'حلقات سحب خلفية على شكل حرف D',
              },
              image: 'images/2382/SUV_package_ring.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'Z6X',
          brandItemCode: 'PA28',
        },
        x7vtyripoufcgr0: {
          label: {
            en: 'Extreme Off Road Package',
            ar: 'فئة الطرق الوعرة القصوى',
          },
          image: 'images/2382/SUT_SKID.jpg',
          category: {
            en: 'Packages',
            ar: 'الفئات',
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            KW: 0,
            default: 0,
          },
          cardType: 'cart',
          packageDescription: {
            en: 'The Extreme Off Road Package comes with 18" wheels, Underbody Armor, FR. Elockers/RR. Simulated Locker, Ultravision Camera System, Side Steps with Integrated Rock Sliders, and Rear O-Ring recovery hooks.',
            ar: 'تأتي حزمة الطرق الوعرة القصوى مع عجلات مقاس 18 إنش، ودروع أسفل الهيكل، وأقفال تفاضلية أمامية إلكترونية / قفل تفاضلي خلفي محاكى، ونظام كاميرا UltraVision، وعتبات جانبية مع منزلقات مدمجة للصخور، وحلقات سحب خلفية على شكل D.',
          },
          accessoryContent: [
            {
              accessoryTitle: {
                en: '18" wheels',
                ar: 'عجلات مقاس 18 إنش',
              },
              accessoryDescription: {
                en: '18" wheels',
                ar: 'عجلات مقاس 18 إنش',
              },
              image: 'images/2382/SUT_wheel.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Underbody Armor',
                ar: 'درع سفلي',
              },
              accessoryDescription: {
                en: 'Underbody Armor',
                ar: 'درع سفلي',
              },
              image: 'images/2382/SUT_SKID.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'FR. Elockers/RR. Simulated Locker',
                ar: 'أقفال تفاضلية أمامية كهربائية / قفل خلفي محاكى',
              },
              accessoryDescription: {
                en: 'FR. Elockers/RR. Simulated Locker',
                ar: 'أقفال تفاضلية أمامية كهربائية / قفل خلفي محاكى',
              },
              image: 'images/2382/SUT_elocker.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Ultravision Camera System',
                ar: 'نظام كاميرات UltraVision ',
              },
              accessoryDescription: {
                en: 'Ultravision Camera System',
                ar: 'نظام كاميرات UltraVision ',
              },
              image: 'images/2382/ultravision_camera.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Side Steps with Integrated Rock Sliders',
                ar: 'عتبات جانبية مزودة بألواح مدمجة للحماية من الصخور',
              },
              accessoryDescription: {
                en: 'Side Steps with Integrated Rock Sliders',
                ar: 'عتبات جانبية مزودة بألواح مدمجة للحماية من الصخور',
              },
              image: 'images/2382/SUT_Side_steps.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
            {
              accessoryTitle: {
                en: 'Rear D-Ring recovery hooks',
                ar: 'حلقات سحب خلفية على شكل حرف D',
              },
              accessoryDescription: {
                en: 'Rear D-Ring recovery hooks',
                ar: 'حلقات سحب خلفية على شكل حرف D',
              },
              image: 'images/2382/SUT_Ring.jpg',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              },
            },
          ],
          autoIntelRPOCode: 'Z6X',
          brandItemCode: 'PA28',
        },
      },
      interactions: {
        a9jk90o1h6fp: {
          label: {
            en: 'Doors',
            ar: 'الأبواب',
          },
          type: 'interaction',
        },
        ygmre5317vts: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة',
          },
          type: 'interaction',
        },
        q5wycu9jmjsc: {
          label: {
            en: 'Headlight',
            ar: 'المصابيح الأمامية',
          },
          type: 'interaction',
        },
        ehdd0bdxbb0i: {
          label: {
            en: 'E-Trunk',
            ar: 'صندوق أمامي كهربائي ETRUNK',
          },
          type: 'interaction',
        },
        yi26t2yj1ipi: {
          label: {
            en: 'Taillight',
            ar: 'المصابيح الخلفية',
          },
          type: 'interaction',
        },
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج',
          },
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقاعد الصف الأول',
          },
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقاعد الصف الثاني',
          },
        },
      },
      name: {
        en: 'GMC Hummer EV',
        ar: 'جي إم سي هامر EV',
      },
      specifications: {
        heading: {
          en: 'GMC Hummer EV',
          ar: 'جي إم سي هامر EV',
        },
        trim: {
          en: 'EV3x',
          ar: 'EV3x',
        },
        price: {
          en: 'Starting from 519000 MSRP',
          ar: 'ابتداء من 519000 MSRP',
        },
        model: 'GMC Hummer EV',
        seatingCapacity: '5',
        dimensions: {
          length: '5250 mm(SUV)/ 5507 mm(PICKUP)',
          width: '',
          height: '',
          wheelbase: '3218 mm(SUV)/ 3444 mm(PICKUP)',
          groundClearance: '',
        },
        performance: [
          {
            value: '830HP (SUV)/1000HP (PICKUP)',
            unit: '',
            label: {
              en: 'Horsepower',
              ar: 'حصان',
            },
          },
          {
            value: '15,592NM (SUV)/15,592NM (PICKUP)',
            unit: '',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران',
            },
          },
          {
            value: '480KM (SUV)/529KM (PICKUP)',
            unit: '',
            label: {
              en: '*Range',
              ar: '*المدى',
            },
          },
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'الطراز',
            },
            subtitle: {
              en: 'GMC Hummer',
              ar: 'GMC Hummer',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية',
            },
            subtitle: {
              en: 'Void Black, Tide Metallic, Interstellar White, Meteorite Metallic, Afterburner Tintcoat, Supernova Metallic, Deep Aurora Metallic.',
              ar: 'أسود فويد، تايد معدني، أبيض فلكي، نيزكي معدني، أفتر بيرنر بطبقتين، سوبرنوفا معدني، أورورا معدني داكن.',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Interior',
              ar: 'الألوان الداخلية',
            },
            subtitle: {
              en: 'Lunar Horizon, Lunar Shadow',
              ar: 'الأفق القمري، الظل القمري',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات',
            },
            subtitle: {
              en: '3218 mm(SUV)/ 3444 mm(PICKUP)',
              ar: '3218 ملم (الدفع الرباعي) / 3444 ملم (بيك أب)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Length',
              ar: 'الطول',
            },
            subtitle: {
              en: '5250 mm(SUV)/ 5507 mm(PICKUP)',
              ar: '5250 ملم (الدفع الرباعي) / 5507 ملم (بيك أب)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Driving Range',
              ar: 'مدى القيادة',
            },
            subtitle: {
              en: 'Upto 480 Km (SUV)/Upto 529 Km (PICKUP)',
              ar: 'حتى 480 كم ‏(الدفع الرباعي) / ‏حتى 529 كم (بيك أب)',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar Basics',
              ar: 'باقة اونستار الأساسية',
            },
            subtitle: {
              en: '8 Years',
              ar: '8 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'OnStar One',
              ar: 'باقة اونستار ون',
            },
            subtitle: {
              en: 'Upto 3 Years',
              ar: 'حتى 3 سنوات',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'عدد الركاب',
            },
            subtitle: {
              en: '5',
              ar: '5',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه',
            },
            subtitle: {
              en: '13.4" Infotainment Display',
              ar: 'شاشة معلومات وترفيه مقاس 13.4 إنش',
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
          },
        ],
      },
      // ── Presentation-mode journeys (SUV vs Pickup) ──────────────────────────
      // Same mechanism as Corvette's Stingray/GS X (see src/utils/selectedJourney.ts):
      // the presentation screen shows one card per journey; picking one filters the
      // configurator to that journey's variants and swaps the heading / spec-details
      // panel. Membership is explicit (variantIds).
      journeys: [
        {
          key: 'suv',
          name: {
            en: 'Hummer EV3x SUV',
            ar: 'Hummer EV3x SUV',
          },
          image: 'images/2382/SUV_variant.png',
          defaultVariant: 'suv',
          variantIds: ['suv'],
          specifications: {
            heading: {
              en: 'GMC Hummer EV SUV',
              ar: 'جي إم سي هامر EV إس يو في',
            },
          },
        },
        {
          key: 'pickup',
          name: {
            en: 'Hummer EV3x PICKUP',
            ar: 'Hummer EV3x PICKUP',
          },
          image: 'images/2382/SUT_Variant.png',
          defaultVariant: 'pickup',
          variantIds: ['pickup'],
          specifications: {
            heading: {
              en: 'GMC Hummer EV Pickup',
              ar: 'جي إم سي هامر EV بيك أب',
            },
          },
        },
      ],
    },
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: {
        en: 'Select Body Style',
        ar: 'اختر نمط الهيكل',
      },
      availableTrims: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      driveType: {
        en: 'Drive Type',
        ar: 'نوع القيادة',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: {
        en: 'Loading',
        ar: 'جارٍ التحميل',
      },
      variantLoading: {
        en: 'Trim is loading',
        ar: 'نموذج جارٍ التحميل',
      },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      disclaimerNote1: {
        en: '*The range (NEDC) will vary depending on vehicle packaging based on a full charge, GM estimated.Actual range will vary based on several factors including temperature, terrain, battery age, loading, use and maintainence.',
        ar: '*مدى القيادة (NEDC) يختلف حسب تجهيزات السيارة بناءً على شحن كامل، وفقًا لتقديرات جنرال موتورز. يختلف المدى الفعلي بناءً على عدة عوامل منها درجة الحرارة والتضاريس وعمر البطارية والحمل والاستخدام والصيانة.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: {
        en: 'Go Back',
        ar: 'العودة',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'عرض مميز',
      },
      configurator: {
        en: 'Configurator',
        ar: 'المُكوِّن',
      },
      buildYourRide: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
      saveYourBuild: {
        en: 'Save Your Build',
        ar: 'احفظ تكوينك',
      },
      startingPrice: {
        en: 'Starting Price',
        ar: 'السعر الابتدائي',
      },
      msrp: {
        en: 'MSRP',
        ar: 'سعر التجزئة المقترح',
      },
      proceed: {
        en: 'Proceed',
        ar: 'متابعة',
      },
    },
    tabs: {
      config: {
        en: 'Config',
        ar: 'الإعداد',
      },
      model: {
        en: 'Model',
        ar: 'الموديل',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلي',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      summary: {
        en: 'Summary',
        ar: 'الملخص',
      },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: {
        en: 'Remove',
        ar: 'إزالة',
      },
      packageCategory: {
        en: 'Package',
        ar: 'حزمة',
      },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: {
        en: 'Save to WhatsApp',
        ar: 'حفظ على واتساب',
      },
      optionEmail: {
        en: 'Save to Email',
        ar: 'حفظ على البريد الإلكتروني',
      },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyReadMoreUrl: {
        en: 'https://www.gmcarabia.com/ae-en/privacy',
        ar: 'https://www.gmcarabia.com/ae-ar/privacy',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: {
        en: 'Main Menu',
        ar: 'القائمة الرئيسية',
      },
      search: {
        en: 'Search',
        ar: 'بحث',
      },
      settings: {
        en: 'Settings',
        ar: 'الإعدادات',
      },
      profile: {
        en: 'Profile',
        ar: 'الملف الشخصي',
      },
      notifications: {
        en: 'Notifications',
        ar: 'الإشعارات',
      },
      help: {
        en: 'Help & Support',
        ar: 'المساعدة والدعم',
      },
      filter: {
        en: 'Filter Options',
        ar: 'خيارات التصفية',
      },
      favorites: {
        en: 'Favorites',
        ar: 'المفضلة',
      },
      history: {
        en: 'History',
        ar: 'التاريخ',
      },
      tools: {
        en: 'Tools',
        ar: 'الأدوات',
      },
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'ألوان المقصورة',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      step: {
        en: 'Step',
        ar: 'خطوة',
      },
      openPanel: {
        en: 'Open Panel',
        ar: 'فتح اللوحة',
      },
      closePanel: {
        en: 'Close Panel',
        ar: 'إغلاق اللوحة',
      },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: {
        en: 'Ext',
        ar: 'خارجي',
      },
      int: {
        en: 'Int',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
    },
    bottomDrawer: {
      variants: {
        en: 'Variants',
        ar: 'الأنواع',
      },
      interactions: {
        en: 'Interactions',
        ar: 'التفاعلات',
      },
      accessories: {
        en: 'Options & Accessories',
        ar: 'الإكسسوارات',
      },
      features: {
        en: 'Features',
        ar: 'الميزات',
      },
      highlights: {
        en: 'Highlights',
        ar: 'الميزات البارزة',
      },
      moreOptions: {
        en: 'More Options',
        ar: 'المزيد من الخيارات',
      },
      colorSelector: {
        en: 'Color Selector',
        ar: 'محدد اللون',
      },
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      wheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      roofs: {
        en: 'Roofs',
        ar: 'الأسقف',
      },
      performance: {
        en: 'Performance',
        ar: 'الأداء',
      },
      packages: {
        en: 'Packages',
        ar: 'الحزم',
      },
      chargingOptions: {
        en: 'Charging Options',
        ar: 'خيارات الشحن',
      },
      safetyServices: {
        en: 'Safety & Services',
        ar: 'السلامة والخدمات',
      },
      expand: {
        en: 'Expand',
        ar: 'توسيع',
      },
      minimize: {
        en: 'Minimize',
        ar: 'تصغير',
      },
      freeRoamView: {
        en: 'Free Roam View',
        ar: 'عرض التجوال الحر',
      },
      featureView: {
        en: 'Featured View',
        ar: 'العرض المميز',
      },
    },
    qr: {
      scan: {
        en: 'Scan QR',
        ar: 'امسح رمز الاستجابة السريعة',
      },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: {
        en: 'Share Configuration',
        ar: 'مشاركة التكوين',
      },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my GMC Hummer EV configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my GMC Hummer EV configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My GMC Hummer EV Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom GMC Hummer EV configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my GMC Hummer EV Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream GMC Hummer EV. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: {
        en: 'Or copy link:',
        ar: 'أو انسخ الرابط:',
      },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: {
        en: 'Copy',
        ar: 'نسخ',
      },
      saveAndShare: {
        en: 'Save & Share',
        ar: 'حفظ ومشاركة',
      },
      copyLink: {
        en: 'Copy link',
        ar: 'نسخ الرابط',
      },
      facebook: {
        en: 'Facebook',
        ar: 'Facebook',
      },
      twitter: {
        en: 'Twitter',
        ar: 'Twitter',
      },
      gmail: {
        en: 'Gmail',
        ar: 'Gmail',
      },
      whatsapp: {
        en: 'Whatsapp',
        ar: 'Whatsapp',
      },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: {
        en: 'ex showroom price',
        ar: 'سعر صالة العرض',
      },
      leads: {
        en: 'Save',
        ar: 'حفظ',
      },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      item: {
        en: 'Item',
        ar: 'العنصر',
      },
      items: {
        en: 'Items',
        ar: 'عناصر',
      },
      totalToPay: {
        en: 'Total to pay',
        ar: 'الإجمالي للدفع',
      },
      bookTestDrive: {
        en: 'Save',
        ar: 'حفظ',
      },
      requestQuote: {
        en: 'Request a Quote',
        ar: 'طلب عرض سعر',
      },
      downloadBrochure: {
        en: 'Download Brochure',
        ar: 'تحميل الكتيب',
      },
    },
    priceSummary: {
      title: {
        en: 'Price Summary',
        ar: 'ملخص السعر',
      },
      enterLocation: {
        en: 'Enter Location',
        ar: 'أدخل الموقع',
      },
      selectCountry: {
        en: 'Location',
        ar: 'الموقع',
      },
      selectCity: {
        en: 'Location',
        ar: 'الموقع',
      },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: {
        en: 'Price Breakdown',
        ar: 'تفصيل السعر',
      },
      color: {
        en: 'Color',
        ar: 'اللون',
      },
      variant: {
        en: 'Variant',
        ar: 'الفئة',
      },
      included: {
        en: 'Included',
        ar: 'مشمول',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    featureDialog: {
      playPause: {
        en: 'Play-Pause',
        ar: 'تشغيل-إيقاف',
      },
    },
    selectDealership: {
      title: {
        en: 'Select a Dealer',
        ar: 'اختر وكيلاً',
      },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      dealersInCity: {
        en: 'Dealers in {{city}}',
        ar: 'وكلاء في {{city}}',
      },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: {
        en: 'UAE',
        ar: 'الإمارات',
      },
      KSA: {
        en: 'KSA',
        ar: 'السعودية',
      },
      KW: {
        en: 'Kuwait',
        ar: 'الكويت',
      },
      default: {
        en: 'UAE',
        ar: 'الإمارات',
      },
    },
    localMatches: {
      exterior: {
        en: 'Exterior',
        ar: 'خارجي',
      },
      interior: {
        en: 'Interior',
        ar: 'داخلي',
      },
      dualTone: {
        en: 'Dual-tone',
        ar: 'لون مزدوج',
      },
      yourConfiguration: {
        en: 'Current Configuration',
        ar: 'التكوين الحالي',
      },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: {
        en: 'Your Build Details',
        ar: 'تفاصيل بنائك',
      },
      options: {
        en: 'Options',
        ar: 'الخيارات',
      },
      save: {
        en: 'Save',
        ar: 'حفظ',
      },
      dealersIn: {
        en: 'Dealers in',
        ar: 'وكلاء في',
      },
      changeDealer: {
        en: 'Change Dealer',
        ar: 'تغيير الوكيل',
      },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: {
        en: 'Matches found',
        ar: 'تطابق تم العثور عليه',
      },
      startingFrom: {
        en: 'Starting from',
        ar: 'يبدأ من',
      },
      getQuote: {
        en: 'Proceed',
        ar: 'احصل على عرض سعر',
      },
      viewDifferences: {
        en: 'View Differences',
        ar: 'عرض الاختلافات',
      },
      match: {
        en: 'Match',
        ar: 'تطابق',
      },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: {
        en: 'Build Details',
        ar: 'تفاصيل التكوين',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: {
        en: 'Exact Match',
        ar: 'تطابق تام',
      },
      similarMatch: {
        en: 'Close Match',
        ar: 'تطابق قريب',
      },
      availableNow: {
        en: 'Available Now',
        ar: 'متوفر الآن',
      },
      whatYouConfigured: {
        en: 'What you configured',
        ar: 'ما قمت بتكوينه',
      },
      matchingInStock: {
        en: 'Matching vehicle available in stock',
        ar: 'مركبة مطابقة متوفرة في المخزون',
      },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: {
        en: 'Close Match Details',
        ar: 'تفاصيل التطابق القريب',
      },
      matchesYourSelection: {
        en: 'Matches your selection',
        ar: 'يطابق اختيارك',
      },
      sameAsWhatYouSelected: {
        en: 'Same as what you selected',
        ar: 'نفس ما اخترته',
      },
      replacesYourSelection: {
        en: 'Replaces your selection',
        ar: 'يستبدل اختيارك',
      },
      selectionNotIncluded: {
        en: 'Selection not included',
        ar: 'الاختيار غير مشمول',
      },
      includedInThisVehicle: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      additionalFeatureAlreadyIncluded: {
        en: 'Additional feature already included',
        ar: 'ميزة إضافية مشمولة بالفعل',
      },
      stockDisclaimer: {
        en: 'Available stock may include extra features not selected in your original configuration.',
        ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.',
      },
      additionalInstalledOptionsLegend: {
        en: 'Included in this vehicle',
        ar: 'مشمول في هذه المركبة',
      },
      vehicleDetails: {
        en: 'Vehicle Details',
        ar: 'تفاصيل المركبة',
      },
      installedOptionsTitle: {
        en: 'Comes with vehicle',
        ar: 'يأتي مع المركبة',
      },
      additionalInstalledOptionsSubtitle: {
        en: 'Additional Installed Options:',
        ar: 'الخيارات المركبة الإضافية:',
      },
      specTrim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      specExteriorColor: {
        en: 'Exterior Color',
        ar: 'اللون الخارجي',
      },
      specInteriorColor: {
        en: 'Interior Color',
        ar: 'اللون الداخلي',
      },
      specWheels: {
        en: 'Wheels',
        ar: 'العجلات',
      },
      specPackage: {
        en: 'Package',
        ar: 'الحزمة',
      },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: {
        en: 'Trim Selection',
        ar: 'اختيار الفئة',
      },
    },
    paymentPage: {
      exteriorColor: {
        en: 'Exterior Color',
        ar: 'لون الهيكل',
      },
      interiorColor: {
        en: 'Interior Color',
        ar: 'لون المقصورة',
      },
      accessory: {
        en: 'Accessory',
        ar: 'ملحق',
      },
      closePayment: {
        en: 'Close',
        ar: 'إغلاق',
      },
    },
    packageModal: {
      includes: {
        en: 'Includes:',
        ar: 'يشمل:',
      },
      of: {
        en: 'of',
        ar: 'من',
      },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: {
        en: 'Item',
        ar: 'غرض',
      },
      items: {
        en: 'Items',
        ar: 'أغراض',
      },
      addPackage: {
        en: 'Add Package',
        ar: 'إضافة الحزمة',
      },
      noThanks: {
        en: 'No Thanks',
        ar: 'لا شكراً',
      },
      alreadyApplied: {
        en: 'Already Applied',
        ar: 'مُطبّق بالفعل',
      },
    },
    changesRequired: {
      en: 'Changes Required',
      ar: 'التغييرات المطلوبة',
    },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: {
      en: 'Apply Changes',
      ar: 'تطبيق التغييرات',
    },
    previewFeature: {
      en: 'Preview Feature',
      ar: 'معاينة الميزة',
    },
    exploreFeatures: {
      en: 'Explore Features',
      ar: 'استكشف الميزات',
    },
    buildAndPrice: {
      en: 'Build & Price',
      ar: 'البناء والسعر',
    },
    features: {
      preview: {
        en: 'Preview Feature',
        ar: 'معاينة المميزات',
      },
      playAll: {
        en: 'PLAY ALL',
        ar: 'تشغيل الكل',
      },
      title: {
        en: 'Features',
        ar: 'المميزات',
      },
    },
    header: {
      localMatches: {
        en: 'Local\nMatches',
        ar: 'التطابقات\nالمحلية',
      },
      matchingVehicles: {
        en: 'Matching vehicles',
        ar: 'مركبات مطابقة',
      },
      configurationsFound: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
    },
    configPanel: {
      playAll: {
        en: 'Play All',
        ar: 'تشغيل الكل',
      },
      pause: {
        en: 'Pause',
        ar: 'إيقاف مؤقت',
      },
      exteriorColors: {
        en: 'Exterior Colors',
        ar: 'ألوان الهيكل الخارجي',
      },
      interiorColors: {
        en: 'Interior Colors',
        ar: 'ألوان الداخلية',
      },
      roofOptions: {
        en: 'Roof Options',
        ar: 'خيارات السقف',
      },
      interiorOptions: {
        en: 'Interior Options',
        ar: 'خيارات الداخلية',
      },
      exteriorAccessories: {
        en: 'Exterior Accessories',
        ar: 'إكسسوارات خارجية',
      },
      noAdditionalOptions: {
        en: 'No additional options available for the selected trim.',
        ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.',
      },
      noFilters: {
        en: 'No filters available.',
        ar: 'لا توجد فلاتر متاحة.',
      },
      noVariantsMatch: {
        en: 'No trims match the selected filters.',
        ar: 'لا توجد فئات تطابق الفلاتر المختارة.',
      },
      configurationBreakdown: {
        en: 'Configuration Breakdown',
        ar: 'تفاصيل التكوين',
      },
      yourConfiguration: {
        en: 'Your Configuration',
        ar: 'تكوينك',
      },
      trim: {
        en: 'Trim',
        ar: 'الفئة',
      },
      total: {
        en: 'Total',
        ar: 'الإجمالي',
      },
    },
    conflictDialog: {
      description: {
        en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.',
        ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.',
      },
    },
    viewport: {
      changeBackground: {
        en: 'Change Background',
        ar: 'تغيير الخلفية',
      },
      selectLanguage: {
        en: 'Switch Language (عربي)',
        ar: 'تبديل اللغة (EN)',
      },
      menu: {
        en: 'Menu',
        ar: 'القائمة',
      },
      exploreFeatures: {
        en: 'Explore Features',
        ar: 'استكشف الميزات',
      },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - مدينة حائل طريق الملك عبدالله. يشيد',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '295892',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع محمد بن القاسم، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '122225',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Mohammed Bin Al Qassem Street, Al Rai, Abdul Hameed Salem Sons Co, Al Rai',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني, شارع محمد بن القاسم, الري, شركة أبناء عبد الحميد سالم, الري',
        },
      },
      {
        id: 2,
        country: 'KW',
        value: 'KW',
        dealerId: '260852',
        dealerName: 'Mohammed Saleh & Reza Yousuf Behbehani Co',
        label: {
          en: 'Mohammed Saleh & Reza Yousuf Behbehani Co., Omar Ibn Al Khatab Street, Sharq',
          ar: 'شركة محمد صالح ورضا يوسف بهبهاني، شارع عمر بن الخطاب، شرق',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475 شارع الشيخ. طريق جابر الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية (منطقة حرة)، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'ماك إنترناشونال إف زي إي، منطقة كردستان، شارع ١٠٠ متر',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '122234',
        dealerName: 'Mannai Trading Company',
        label: {
          en: 'Mannai Trading Company W.L.L., Salwa E Industrial Street',
          ar: 'شركة مناعي التجارية ذ.م.م، شارع سلوى إي الصناعي',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '295854',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Nahda Main Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق النهضة الرئيسي',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260877',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, Al Firq Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260880',
        dealerName: 'Moosa',
        label: {
          en: 'Moosa Abdul Rahman Hassan & Co. LLC, New Industrial Road',
          ar: 'موسى عبد الرحمن حسن وشركاه ش.م.م، طريق الصناعية الجديدة',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122227',
        dealerName: 'RYMCO',
        label: {
          en: 'Rasamny Younis Motor Co. SAL, Michael Zakhour Road',
          ar: 'شركة رسامني يونس للسيارات (ش.م.ل)، طريق مايكل زخور',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - القوز 3، المنطقة الصناعية، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد - صالة عرض , الخبيصي, ديرة, دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: false,
    showLocalMatches: true,
    showDisclaimerInPresentation: true,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false,
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'hummer',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Hummer',
    modelName: 'Hummer EV',
    pageTitle: 'Hummer Configurator',
    client: 'GM',
    brand: 'gmc',
    modelId: 'GCHUMSUT2025',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      provider: 'adobe',
      brand: 'gmc',
      modelName: 'hummer',
      vehicleType: 'suv',
    },
    presentation: {
      video: 'videos/2382/presentation_video_1.webm',
      loadingImage: 'images/2382/sliderImage.webp',
      transitionImage: 'images/2382/transition_logo.png',
      headerLogo: 'images/2382/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: '',
          },
          value: {
            en: '830HP (SUV)/1000HP (PICKUP)',
            ar: '830 حصان (الدفع الرباعي)  / 1000 حصان (بيك أب)',
          },
          unit: '',
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران',
          },
          value: {
            en: '15,592NM (SUV)/15,592NM (PICKUP)',
            ar: '15,592 نيوتن متر (الدفع الرباعي) / 15,592 نيوتن متر (بيك أب)',
          },
          unit: '',
        },
        {
          index: 2,
          show: true,
          title: {
            en: '*Range',
            ar: '*المدى',
          },
          value: {
            en: '480KM (SUV)/529KM (PICKUP)',
            ar: '480 كم (الدفع الرباعي) / 529 كم (بيك أب)',
          },
          unit: '',
        },
      ],
    },
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: [
          'email',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: [
          'whatsapp',
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; the booking forms and Save to WhatsApp
        // require it (GM needs a dealer on WhatsApp leads); Email stays optional.
        requiredFor: [
          'test-drive',
          'callback',
          'whatsapp',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: [
          'test-drive',
          'callback',
        ],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-77110a86d4ec-staging.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true,
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true,
          },
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true,
          },
        ],
      },
    },
  },
  filterOptions: [],
  styleConfig: {
    cssVariables: {
      '--default-font-family': '\'Stratum GMC\', \'Chevy Sans\', sans-serif',
      '--bold-font-family': '\'Stratum GMC\', \'Chevy Sans Medium\', sans-serif',
      '--brand-primary': '#B9081B',
      '--brand-primary-hover': '#970716',
      '--brand-secondary': '#CC0000',
      '--brand-accent': '#B9081B',
      '--brand-accent-hover': '#970716',
      '--brand-accent-light': '#f7e4e9',
      '--brand-surface': '#f3f3f3',
      '--brand-surface-alt': '#f3f4f6',
      '--brand-text': '#000000',
      '--brand-text-secondary': '#666666',
      '--brand-text-tertiary': '#bebebe',
      '--brand-border': '#e6e6e6',
      '--brand-border-selected': '#B9081B',
      '--brand-card-bg': '#ffffff',
      '--brand-toggle-gradient-from': '#B9081B',
      '--brand-toggle-gradient-to': '#CC0000',
      '--brand-toggle-knob-border': '#B9081B',
      '--brand-accent-secondary': '#CC0000',
      '--brand-card-radius': '2px',
      '--brand-card-radius-sm': '2px',
      '--brand-card-radius-lg': '3px',
      '--brand-button-radius': '2px',
      '--brand-button-radius-sm': '2px',
      '--brand-pill-radius': '50px',
      '--brand-cta-radius': '2px',
      '--brand-button-border': 'var(--brand-accent)',
      '--brand-cta-arrow-bg': '#B9081B',
      '--brand-cta-arrow-fg': '#ffffff',
      '--brand-cta-shimmer': '#B9081B',
      '--brand-cta-loading-bg': '#B9081B',
    },
  },
} as unknown as UIMetadataComplete;
